<template>
  <div class="virus-database">
    <div class="content-section">
      <!-- 搜索区域 -->
      <div class="search-section">
        <div class="search-wrapper">
          <el-input
            v-model="searchQuery"
            placeholder="Search by host or virus name..."
            :prefix-icon="Search"
            clearable
            @input="handleSearch"
            class="search-input"
          >
            <template #append>
              <el-button :icon="Search" @click="handleSearch">
                Search
              </el-button>
            </template>
          </el-input>
        </div>
      </div>

      <div class="table-section">
        <div class="table-header">
          <h2>Phage Data</h2>
          <div class="table-actions">
            <el-select v-model="pageSize" @change="handleSizeChange" class="page-size-select">
              <el-option
                v-for="size in [10, 20, 50, 100]"
                :key="size"
                :label="size + ' items per page'"
                :value="size"
              />
            </el-select>
          </div>
        </div>

        <el-table
          v-loading="loading"
          :data="viruses"
          style="width: 100%"
          border
          stripe
          highlight-current-row
          class="custom-table"
        >
          <el-table-column
            type="index"
            label="#"
            width="60"
            align="center"
          />
          <el-table-column
            prop="host"
            label="Host"
            sortable
            min-width="200"
            show-overflow-tooltip
          >
            <template #default="scope">
              <div class="host-name">
                <span class="host-text">{{ scope.row.host }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="virus_name"
            label="Virus(Phage) Name/ID"
            sortable
            min-width="200"
            show-overflow-tooltip
          >
            <template #default="scope">
              <span>{{ scope.row.virus_name }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="size_bp"
            label="Size (bp)"
            sortable
            width="120"
            align="right"
          >
            <template #default="scope">
              <span class="size-value">{{ formatNumber(scope.row.size_bp) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            prop="shape"
            label="Shape"
            width="100"
            align="center"
          >
            <template #default="scope">
              <el-tag
                :type="getShapeTag(scope.row.shape)"
                size="small"
              >
                {{ scope.row.shape }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            label="Actions"
            width="200"
            fixed="right"
            align="center"
          >
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  type="primary"
                  :icon="View"
                  circle
                  @click="viewDetails(scope.row)"
                />
                <el-button
                  type="success"
                  :icon="Download"
                  circle
                  @click="downloadGenome(scope.row)"
                />
              </div>
            </template>
          </el-table-column>
        </el-table>

        <div class="pagination">
          <el-pagination
            v-model:current-page="currentPage"
            v-model:page-size="pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            background
          />
        </div>
      </div>

      <!-- 病毒详情对话框 -->
      <el-dialog
        v-model="detailsDialogVisible"
        title="Virus Details"
        width="90%"
        class="virus-dialog"
        :fullscreen="true"
        destroy-on-close
      >
        <div v-if="selectedVirus" class="virus-details">
          <div class="detail-section">
            <el-descriptions :column="2" border>
              <el-descriptions-item label="Host">{{ selectedVirus.host }}</el-descriptions-item>
              <el-descriptions-item label="Virus Name">{{ selectedVirus.virus_name }}</el-descriptions-item>
              <el-descriptions-item label="Size (bp)">{{ formatNumber(selectedVirus.size_bp) }}</el-descriptions-item>
              <el-descriptions-item label="Shape">{{ selectedVirus.shape }}</el-descriptions-item>
            </el-descriptions>
          </div>

          <el-tabs v-model="activeTab" class="sequence-tabs">
            <el-tab-pane label="FASTA Sequence" name="sequence">
              <div class="fasta-content-wrapper">
                <div class="fasta-content" v-if="fastaContent">
                  <pre>{{ fastaContent }}</pre>
                </div>
                <div v-else class="loading-fasta">
                  <el-skeleton :rows="10" animated />
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
        <template #footer>
          <div class="dialog-footer">
            <el-button @click="detailsDialogVisible = false">Close</el-button>
            <el-button type="primary" @click="downloadGenome(selectedVirus)">
              Download Genome
            </el-button>
          </div>
        </template>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { Search, View, Download } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

export default {
  name: 'VirusDatabase',
  setup() {
    const loading = ref(false)
    const searchQuery = ref('')
    const viruses = ref([])
    const currentPage = ref(1)
    const pageSize = ref(20)
    const total = ref(0)
    const detailsDialogVisible = ref(false)
    const selectedVirus = ref(null)
    const fastaContent = ref(null)
    const activeTab = ref('sequence')

    // 加载数据
    const loadData = async () => {
      loading.value = true
      try {
        const response = await axios.get('/api/virus-data/', {
          params: {
            page: currentPage.value,
            page_size: pageSize.value,
            search: searchQuery.value
          }
        })
        if (response.data && response.data.items) {
          viruses.value = response.data.items
          total.value = response.data.total
        } else {
          throw new Error('Invalid response format')
        }
      } catch (error) {
        console.error('Error loading viruses:', error)
        ElMessage.error('Failed to load virus data')
      } finally {
        loading.value = false
      }
    }

    // 查看病毒详情
    const viewDetails = async (virus) => {
      selectedVirus.value = virus
      detailsDialogVisible.value = true
      fastaContent.value = null
      
      try {
        const response = await axios.get(`/api/virus/${virus.id}/fasta-content/`)
        fastaContent.value = response.data.content
      } catch (error) {
        console.error('Error loading FASTA content:', error)
        ElMessage.error('Failed to load FASTA sequence')
      }
    }

    // 下载基因组数据
    const downloadGenome = async (virus) => {
      try {
        // 下载FASTA文件
        const fastaResponse = await axios.get(`/api/virus/${virus.id}/download/fasta/`, { 
          responseType: 'blob' 
        });
        
        // 记录Content-Type，便于调试
        const contentType = fastaResponse.headers['content-type'] || 'application/octet-stream';
        console.log('下载的文件Content-Type:', contentType);
        
        // 使用病毒名称作为文件名，确保文件名正确
        const fileName = `${virus.virus_name}.fasta`;
        console.log('下载文件名:', fileName);
        
        // 下载文件
        downloadFile(fastaResponse.data, fileName);
        
        ElMessage.success('文件下载成功');
      } catch (error) {
        console.error('Error downloading file:', error);
        ElMessage.error('下载文件失败，请稍后重试');
      }
    }

    // 辅助函数：下载文件
    const downloadFile = (data, filename) => {
      // 检查内容类型，通过检查blob的前几个字节判断是否为文本
      const isTextFile = filename.endsWith('.fasta') || filename.endsWith('.fa') || filename.endsWith('.txt');
      
      console.log('下载文件类型:', isTextFile ? '文本文件' : '二进制文件', 'Blob大小:', data.size);
      
      if (isTextFile) {
        try {
          // 使用FileReader将Blob转换为文本
          const reader = new FileReader();
          reader.onload = function() {
            try {
              // 检查读取的文本内容
              const textContent = reader.result;
              const preview = textContent.substring(0, 100).replace(/\n/g, ' ');
              console.log('文件内容预览:', preview);
              
              // 创建文本Blob
              const textBlob = new Blob([textContent], { type: 'text/plain' });
              
              // 创建下载链接
              const url = URL.createObjectURL(textBlob);
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', filename);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
              
              console.log('文本文件下载完成');
            } catch (innerError) {
              console.error('创建文本Blob时出错:', innerError);
              downloadBinaryFile(data, filename);
            }
          };
          
          reader.onerror = function(readerError) {
            console.error('FileReader错误:', readerError);
            downloadBinaryFile(data, filename);
          };
          
          // 读取为文本
          reader.readAsText(data);
        } catch (error) {
          console.error('读取文本时出错:', error);
          // 如果转换失败，回退到二进制下载
          downloadBinaryFile(data, filename);
        }
      } else {
        // 非文本文件直接以二进制下载
        downloadBinaryFile(data, filename);
      }
    }
    
    // 二进制文件下载函数
    const downloadBinaryFile = (data, filename) => {
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }

    // 格式化数字
    const formatNumber = (num) => {
      return num ? num.toLocaleString() : '0'
    }

    // 获取形状标签类型
    const getShapeTag = (shape) => {
      switch (shape.toLowerCase()) {
        case 'circular':
          return 'success'
        case 'linear':
          return 'warning'
        default:
          return 'info'
      }
    }

    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1
      loadData()
    }

    // 处理页面大小变化
    const handleSizeChange = (val) => {
      pageSize.value = val
      loadData()
    }

    // 处理页码变化
    const handleCurrentChange = (val) => {
      currentPage.value = val
      loadData()
    }

    onMounted(() => {
      loadData()
    })

    return {
      loading,
      searchQuery,
      viruses,
      currentPage,
      pageSize,
      total,
      detailsDialogVisible,
      selectedVirus,
      fastaContent,
      activeTab,
      Search,
      View,
      Download,
      handleSearch,
      handleSizeChange,
      handleCurrentChange,
      formatNumber,
      getShapeTag,
      viewDetails,
      downloadGenome,
    }
  }
}
</script>

<style scoped>
.virus-database {
  padding: 20px;
  max-width: 1280px;
  margin: 0 auto;
}

.content-section {
  margin-top: 77px;
}

.search-section {
  margin-bottom: 30px;
}

.search-wrapper {
  max-width: 600px;
  margin: 0 auto;
}

.search-input {
  width: 100%;
}

.table-section {
  background: white;
  border-radius: 12px;
  padding: 25px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.table-header h2 {
  margin: 0;
  font-size: 1.5rem;
  color: #1a1a1a;
}

.custom-table {
  margin-bottom: 20px;
}

.host-name {
  display: flex;
  align-items: center;
}

.host-text {
  color: #1a1a1a;
  font-weight: 500;
}

.size-value {
  font-family: 'Consolas', 'Courier New', monospace;
  color: #1a1a1a;
  font-size: 14px;
  letter-spacing: 0.5px;
}

.action-buttons {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.pagination {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.virus-dialog :deep(.el-dialog__body) {
  padding: 20px;
  max-height: calc(80vh - 150px);
  overflow-y: auto;
}

.virus-details {
  padding: 20px;
}

.detail-section {
  margin-bottom: 20px;
}

.fasta-content-wrapper {
  height: calc(60vh - 150px);
  position: relative;
}

.fasta-content {
  height: 100%;
  overflow-y: auto;
  background: #f5f7fa;
  padding: 15px;
  border-radius: 4px;
  font-family: monospace;
}

.fasta-content pre {
  margin: 0;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: monospace;
  font-size: 14px;
  line-height: 1.5;
}

.loading-fasta {
  padding: 20px;
}

.dialog-footer {
  padding: 20px;
  text-align: right;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
}

:deep(.el-dialog__footer) {
  padding-top: 10px;
  border-top: 1px solid #dcdfe6;
}

@media (max-width: 768px) {
  .table-header {
    flex-direction: column;
    gap: 15px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 5px;
  }
}
</style> 